/**
 * DV-PROJ-001: Upload Asset Modal Component
 *
 * Modal for uploading project assets with file validation and progress
 */

import React, { useState, useRef } from 'react';
import { X, Upload, FileImage, AlertCircle, CheckCircle } from 'lucide-react';
import { useCreateAssetMutation, useFileUploadMutation } from '@/hooks/useProjectsQuery';
import type { Project, AssetType } from '@/types/projects';

interface UploadAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

interface FormErrors {
  file?: string;
  type?: string;
  notes?: string;
}

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'bottle', label: 'Bottle' },
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'logo', label: 'Logo' },
  { value: 'other', label: 'Other' }
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'application/json'
];

const UploadAssetModal: React.FC<UploadAssetModalProps> = ({
  isOpen,
  onClose,
  project
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assetType, setAssetType] = useState<AssetType>('other');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileUploadMutation = useFileUploadMutation();
  const createAssetMutation = useCreateAssetMutation();

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setAssetType('other');
      setNotes('');
      setErrors({});
      setIsUploading(false);
    }
  }, [isOpen]);

  // Validate file
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB';
    }

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return 'File type not supported. Please upload an image, PDF, or text file.';
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setErrors({ ...errors, file: error });
      return;
    }

    setSelectedFile(file);
    setErrors({ ...errors, file: undefined });
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    if (notes && notes.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !selectedFile) {
      return;
    }

    setIsUploading(true);

    try {
      // Upload file
      const uploadResult = await fileUploadMutation.mutateAsync({
        file: selectedFile,
        projectId: project.id,
        type: assetType
      });

      // Create asset record
      await createAssetMutation.mutateAsync({
        projectId: project.id,
        data: {
          type: assetType,
          fileUrl: uploadResult.fileUrl,
          fileName: uploadResult.fileName,
          fileSize: uploadResult.fileSize,
          mimeType: uploadResult.mimeType,
          notes: notes.trim() || undefined
        }
      });

      onClose();
    } catch (error) {
      console.error('Failed to upload asset:', error);
      setErrors({ ...errors, file: 'Upload failed. Please try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-black/90 backdrop-blur-xl border border-blue-500/20 rounded-lg shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-light text-white">Upload Asset</h2>
              <p className="text-xs text-white/50 font-mono">{project.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                File *
              </label>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                           hover:bg-white/5 ${
                  errors.file
                    ? 'border-red-500/50 bg-red-500/10'
                    : selectedFile
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-white/20 hover:border-white/30'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0" />
                    <div className="text-left flex-1 min-w-0">
                      <p className="text-sm font-medium text-white/90 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-white/60">
                        {formatFileSize(selectedFile.size)} • {selectedFile.type}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <FileImage className="w-8 h-8 text-white/40 mx-auto mb-2" />
                    <p className="text-sm text-white/70 mb-1">
                      Drag and drop your file here, or click to browse
                    </p>
                    <p className="text-xs text-white/40">
                      Images, PDFs, and text files up to 10MB
                    </p>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES.join(',')}
                onChange={handleFileInputChange}
                className="hidden"
              />

              {errors.file && (
                <div className="flex items-center gap-2 mt-2 text-red-400 text-xs">
                  <AlertCircle className="w-3 h-3" />
                  {errors.file}
                </div>
              )}
            </div>

            {/* Asset Type */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Asset Type *
              </label>
              <select
                value={assetType}
                onChange={(e) => setAssetType(e.target.value as AssetType)}
                className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white/90
                           focus:outline-none focus:border-blue-500/50 transition-colors font-mono text-sm"
              >
                {ASSET_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this asset"
                rows={3}
                className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                           focus:outline-none transition-colors font-mono text-sm resize-none ${
                  errors.notes
                    ? 'border-red-500/50 focus:border-red-500/70'
                    : 'border-white/20 focus:border-blue-500/50'
                }`}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.notes ? (
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.notes}
                  </div>
                ) : (
                  <div />
                )}
                <div className="text-xs text-white/40 font-mono">
                  {notes.length}/500
                </div>
              </div>
            </div>

            {/* Error Message */}
            {(fileUploadMutation.isError || createAssetMutation.isError) && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to upload asset. Please try again.
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-sm border border-white/20 text-white/70 rounded
                         hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !selectedFile}
              className="px-4 py-2 text-sm bg-blue-500/20 border border-blue-500/50 text-blue-400 rounded
                         hover:bg-blue-500/30 transition-colors disabled:opacity-50
                         flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Asset
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadAssetModal;